import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { computeStickerTextLayout, useStickerFontsReady } from "./fitText";

/**
 * Renderiza texto curvado en SVG usando <textPath>. Se coloca dentro de un
 * <svg> superpuesto al rect del texto. Soporta stroke (contorno), relleno
 * y alineación (left/center/right) en el recorrido del path.
 *
 * Nota sobre fuentes: en `src/index.css` existe un reset global
 * `* { font-family: "Nunito Sans" !important; }` que también alcanza a los
 * `<text>` SVG (los SVG presentation attributes tienen menor prioridad que
 * el !important global). Para saltarnos eso sin tocar el reset, inyectamos
 * un <style> scoped dentro del propio SVG con !important.
 */
const CurvedTextLayer = ({ layout, isPlaceholder }) => {
	const reactId = useId();
	const pathId = `stk-path-${reactId.replace(/:/g, "")}`;
	const textClass = `stk-text-${reactId.replace(/:/g, "")}`;
	const startOffset =
		layout.align === "left"
			? "0%"
			: layout.align === "right"
				? "100%"
				: "50%";
	const textAnchor =
		layout.align === "left"
			? "start"
			: layout.align === "right"
				? "end"
				: "middle";
	// IMPORTANTE: target tanto el <text> como todos sus descendientes
	// (incluye <textPath>). El reset global `* { font-family: ... !important }`
	// también matchea <textPath> directamente, así que la herencia desde <text>
	// no basta — hay que aplicar la fuente con !important al propio <textPath>.
	const scopedCss = `
		.${textClass},
		.${textClass} * {
			font-family: ${layout.fontFamily} !important;
			font-weight: ${layout.fontWeight} !important;
			font-style: ${layout.fontStyle} !important;
			font-size: ${layout.fontSize}px !important;
			letter-spacing: ${layout.letterSpacing}px;
			paint-order: stroke fill;
		}
	`;
	return (
		<Box
			sx={{
				position: "absolute",
				left: layout.rect.x,
				top: layout.rect.y,
				width: layout.rect.width,
				height: layout.rect.height,
				pointerEvents: "none",
				opacity: isPlaceholder ? 0.75 : 1,
				overflow: "visible",
			}}
		>
			<svg
				width="100%"
				height="100%"
				viewBox={`0 0 ${layout.rect.width} ${layout.rect.height}`}
				preserveAspectRatio="none"
				style={{ overflow: "visible", display: "block" }}
			>
				<style>{scopedCss}</style>
				<defs>
					<path id={pathId} d={layout.curve.pathLocal} fill="none" />
				</defs>
				<text
					className={textClass}
					fill={layout.color}
					stroke={layout.stroke || "none"}
					strokeWidth={layout.strokeWidth || 0}
					strokeLinejoin="round"
					textAnchor={textAnchor}
				>
					<textPath href={`#${pathId}`} startOffset={startOffset}>
						{layout.text}
					</textPath>
				</text>
			</svg>
		</Box>
	);
};

/**
 * Vista previa DOM de un sticker personalizable.
 *
 * Renderiza la imagen base y superpone el texto del usuario dentro del área
 * configurada en el sticker. Se reajusta automáticamente cuando cambia el
 * tamaño del contenedor (ResizeObserver) o el texto.
 *
 * Props:
 *  - sticker: objeto del registro de custom stickers (ver data/customStickers)
 *  - value: texto del usuario
 *  - size: tamaño máximo del sticker ("fill" para ocupar el contenedor al 100%
 *    o un número en px para tamaño fijo). Por defecto "fill".
 *  - interactive: si true, muestra un outline al hacer hover (para grids).
 *  - selected: si true, muestra el outline de selección.
 */
const StickerPreview = ({
	sticker,
	value,
	size = "fill",
	interactive = false,
	selected = false,
	onClick,
	ariaLabel,
}) => {
	const containerRef = useRef(null);
	const [measured, setMeasured] = useState({ w: 0, h: 0 });

	useLayoutEffect(() => {
		if (size !== "fill") return;
		const el = containerRef.current;
		if (!el) return;
		const update = () => {
			const w = el.clientWidth;
			if (!w) return;
			const h = w * (sticker.aspectRatio || 1);
			setMeasured({ w, h });
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return () => ro.disconnect();
	}, [size, sticker.aspectRatio]);

	const baseWidth = size === "fill" ? measured.w : size;
	const baseHeight =
		size === "fill" ? measured.h : size * (sticker.aspectRatio || 1);

	// Nos aseguramos de que la tipografía del sticker esté cargada antes de
	// medir, para que el ajuste automático de tamaño no use el fallback.
	// eslint-disable-next-line no-unused-vars
	const fontsReady = useStickerFontsReady(
		sticker.text?.fontFamily ? [sticker.text.fontFamily] : [],
	);

	const layout =
		baseWidth > 0 && sticker.text
			? computeStickerTextLayout(
					value,
					sticker.text,
					baseWidth,
					baseHeight,
				)
			: null;

	const textShadow =
		layout?.textShadowCSS ??
		(layout?.shadow
			? `${layout.shadow.offsetX}px ${layout.shadow.offsetY}px ${layout.shadow.blur}px ${layout.shadow.color}`
			: "none");

	const webkitStroke =
		layout && layout.stroke && layout.strokeWidth
			? `${layout.strokeWidth}px ${layout.stroke}`
			: undefined;

	const isPlaceholder =
		!value || String(value).trim() === "";

	return (
		<Box
			ref={containerRef}
			onClick={onClick}
			role={onClick ? "button" : undefined}
			tabIndex={onClick ? 0 : undefined}
			aria-label={ariaLabel}
			onKeyDown={(e) => {
				if (!onClick) return;
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick(e);
				}
			}}
			sx={{
				position: "relative",
				width: size === "fill" ? "100%" : size,
				aspectRatio: `1 / ${sticker.aspectRatio || 1}`,
				cursor: onClick ? "pointer" : "default",
				borderRadius: 2,
				transition: "transform 0.15s ease, box-shadow 0.15s ease",
				outline: selected ? "3px solid #6366F1" : "3px solid transparent",
				outlineOffset: selected ? "2px" : 0,
				...(interactive && {
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow: "0 6px 18px rgba(15,23,42,0.12)",
					},
					"&:focus-visible": {
						outline: "3px solid #6366F1",
						outlineOffset: "2px",
					},
				}),
			}}
		>
			<img
				src={sticker.baseImage}
				alt=""
				crossOrigin="anonymous"
				draggable={false}
				style={{
					width: "100%",
					height: "100%",
					objectFit: "contain",
					display: "block",
					userSelect: "none",
					pointerEvents: "none",
				}}
			/>
			{layout && layout.curve ? (
				<CurvedTextLayer layout={layout} isPlaceholder={isPlaceholder} />
			) : layout && layout.verticalRotate ? (
				<Box
					sx={{
						position: "absolute",
						left: layout.rect.x,
						top: layout.rect.y,
						width: layout.rect.width,
						height: layout.rect.height,
						display: "flex",
						alignItems: "center",
						justifyContent:
							layout.align === "left"
								? "flex-start"
								: layout.align === "right"
									? "flex-end"
									: "center",
						pointerEvents: "none",
						px: `${layout.rect.width * (layout.verticalRotateInnerPadFrac ?? 0.022)}px`,
						py: `${layout.rect.height * (layout.verticalRotateInnerPadFrac ?? 0.022)}px`,
					}}
				>
					<Box
						component="span"
						sx={{
							fontFamily: `${layout.fontFamily} !important`,
							fontWeight: `${layout.fontWeight} !important`,
							fontStyle: `${layout.fontStyle} !important`,
							fontSize: `${layout.fontSize}px`,
							lineHeight: layout.verticalLineHeight ?? 1.15,
							letterSpacing: `${layout.letterSpacing}px`,
							color: layout.color,
							textShadow,
							WebkitTextStroke: webkitStroke,
							paintOrder: "stroke fill",
							whiteSpace: "nowrap",
							/* Igual que PDF/Konva: comprimir línea y luego girar 90° horario */
							transform: `rotate(90deg) scaleX(${layout.scaleX ?? 1})`,
							transformOrigin: "center center",
							opacity: isPlaceholder ? 0.75 : 1,
							userSelect: "none",
						}}
					>
						{layout.text}
					</Box>
				</Box>
			) : layout && layout.vertical ? (
				<Box
					sx={{
						position: "absolute",
						left: layout.rect.x,
						top: layout.rect.y,
						width: layout.rect.width,
						height: layout.rect.height,
						display: "flex",
						alignItems: "center",
						justifyContent:
							layout.align === "left"
								? "flex-start"
								: layout.align === "right"
									? "flex-end"
									: "center",
						pointerEvents: "none",
						/* Mismos márgenes que fitFontSizeVertical (fitText.js) */
						px: `${layout.rect.width * 0.06}px`,
						py: `${layout.rect.height * 0.06}px`,
					}}
				>
					<Box
						component="span"
						sx={{
							fontFamily: `${layout.fontFamily} !important`,
							fontWeight: `${layout.fontWeight} !important`,
							fontStyle: `${layout.fontStyle} !important`,
							fontSize: `${layout.fontSize}px`,
							/* Una letra por línea como Konva/PDF: mismo line-height que el ajuste */
							lineHeight: layout.verticalLineHeight ?? 1.12,
							letterSpacing: `${layout.letterSpacing}px`,
							color: layout.color,
							textShadow,
							WebkitTextStroke: webkitStroke,
							paintOrder: "stroke fill",
							whiteSpace: "pre-line",
							textAlign: "center",
							opacity: isPlaceholder ? 0.75 : 1,
							userSelect: "none",
						}}
					>
						{Array.from(layout.text).join("\n")}
					</Box>
				</Box>
			) : (
				layout && (
					<Box
						sx={{
							position: "absolute",
							left: layout.rect.x,
							top: layout.rect.y,
							width: layout.rect.width,
							height: layout.rect.height,
							display: "flex",
							alignItems: "center",
							justifyContent:
								layout.align === "left"
									? "flex-start"
									: layout.align === "right"
										? "flex-end"
										: "center",
							pointerEvents: "none",
							px: `${layout.rect.width * 0.04}px`,
						}}
					>
						<Box
							component="span"
							sx={{
								// Usamos !important porque en index.css hay un reset global
								// `* { font-family: "Nunito Sans" !important; }` que se aplica
								// dentro de portales MUI (fuera de #templates-container).
								fontFamily: `${layout.fontFamily} !important`,
								fontWeight: `${layout.fontWeight} !important`,
								fontStyle: `${layout.fontStyle} !important`,
								fontSize: `${layout.fontSize}px`,
								lineHeight: 1.05,
								letterSpacing: `${layout.letterSpacing}px`,
								color: layout.color,
								textAlign: layout.align,
								textShadow,
								WebkitTextStroke: webkitStroke,
								paintOrder: "stroke fill",
								whiteSpace: "nowrap",
								opacity: isPlaceholder ? 0.75 : 1,
								userSelect: "none",
								// Para nombres muy largos comprimimos horizontalmente
								// sin tocar la altura (mantiene proporciones).
								transform:
									layout.scaleX !== 1
										? `scaleX(${layout.scaleX})`
										: "none",
								transformOrigin:
									layout.align === "left"
										? "left center"
										: layout.align === "right"
											? "right center"
											: "center center",
							}}
						>
							{layout.text}
						</Box>
					</Box>
				)
			)}
		</Box>
	);
};

export default StickerPreview;
